/**
 * client/src/pages/Live.tsx — the helper's view. §3.15. Read-only, over SSE.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHAT IT IS FOR
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * "Every failure above degrades into the same phone call — *'What does it say on the screen?'* —
 * asked by someone who cannot see the screen, answered by someone who does not know which words
 * matter. Karti cannot currently distinguish a denied permission from a stalled upload from a cue
 * she did not hear."
 *
 * "It converts the entire failure surface from support calls into fixable moments."
 *
 * This is the screen Karti watches on the video call while his mother records. Everything on it is a
 * fact she is not being shown: the band names, the levels, the coach budget, the `/health` response
 * verbatim, and **the last error in full engineering detail** — which §3.15 calls "the one place in
 * the product where a stack trace is the right answer, because the reader is the person who can act
 * on it."
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * READ-ONLY OVER THE STREAM. ONE WRITE, AND IT IS THE ONE §3.13 ASKS FOR.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * "one writer, one reader, no clock."
 *
 * ⚠️ **Nothing on this page can touch what she is doing.** The stream is an `EventSource`, which is
 * one-way by construction; the alternative — a WebSocket with a `readOnly` boolean in the client —
 * would be one merge away from being wrong, on the screen somebody's son is watching while his
 * mother is mid-sentence. A helper who could advance her prompt from another city is a helper who
 * will, by accident, at the worst moment. That property is unchanged and it is the important one.
 *
 * ⚠️ **The payoff sentence is the exception, and §3.13 puts it here by name.** *"Before she starts,
 * on the same laptop or from his own, Karti types one sentence he wants to hear in his mother's
 * voice. She never sees it."* It is not a control over her session — it cannot advance a prompt,
 * cannot end a take, cannot be observed from her screen at all. It writes one column that only ever
 * reads out at the very end.
 *
 * It had to be somewhere, and everywhere else was worse: the studio is her screen (§3.13 forbids it),
 * `/admin` did not have a route for it, and the only other door was `POST /api/v1/sessions` at
 * creation — which is why, before this field existed, **every session in the product ended
 * `deferred` with a working clone behind it and no payoff.** It goes over an ordinary
 * `PATCH /api/v1/sessions/:id/reveal-text`, not over the stream: SSE stays one-way, and a `fetch`
 * cannot be mistaken for a channel into her session.
 *
 * The other thing this page writes to is the browser it is running in: `setCopyFailureSink` routes
 * a `{name}` that never got filled to Karti instead of to a devtools console nobody has open. That
 * is the other half of the promise in `lib/say.ts`.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * IT IS THE CONSOLE, SO IT SPEAKS ENGINEERING
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `CONSOLE` in `shared/voice/copy.ts` is exempt from §3.2's vocabulary rule and says so: "the
 * internal names are good and they are the right words here." So this page says "Takes drained" and
 * "Coach lines fired", which are exactly the words that would be forbidden two routes away.
 *
 * They still come from `copy.ts`, for the reason that file gives: "a second place for strings is how
 * the first place stops being the only place." Console strings are filled with `say(t, vars, t)` —
 * the fallback is the template itself, because an unfilled `{n}` in front of Karti is a bug report
 * and in front of his mother is a defect.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { LiveEvent, SessionView } from "../../../shared/protocol.ts";
import { CONSOLE } from "../../../shared/voice/copy.ts";
import { COACH_BUDGET } from "../../../shared/voice/policy.ts";
import { ScriptError, promptById } from "../../../shared/voice/script.ts";
import { REVEAL_TEXT_MAX_CHARS } from "../../../shared/voice/policy.ts";
import { api, describeForConsole, openLive, readLiveEvent } from "../lib/api.ts";
import { useSessionHandle } from "../lib/handle.ts";
import { say, setCopyFailureSink } from "../lib/say.ts";
import { Button, ConsoleFrame, Empty, Field, StatusDot } from "../ui/primitives.tsx";

/**
 * How many events to keep. 400.
 *
 * The level channel is the volume driver: `CAPTURE.meterHz` is 20, so a fourteen-minute long session
 * emits on the order of 17 000 level events. Keeping them all is a monotonically growing array on a
 * page that is open for the whole session, on whatever laptop Karti happens to have — and none of
 * them is worth anything ten seconds after it arrives, because the meter shows the *current* level.
 *
 * Level events are therefore folded into a single value and never appended; 400 bounds everything
 * else, which is coach lines, takes, drains, state changes and errors. A ten-prompt session with two
 * retakes each produces well under a hundred, so the cap only ever bites on a pathological stream —
 * which is exactly when an unbounded array would have been the problem.
 */
const KEEP = 400;

type Timed = LiveEvent & { seq: number };

export function Live() {
  /**
   * ⚠️ **The route is `/live/:sessionId` but what usually arrives here is a CLAIM TOKEN.**
   *
   * The server mints the helper's link as `{publicOrigin}/live/{claimToken}`
   * (`server/src/routes/sessions.ts`), and it is right to: the helper is on another laptop in another
   * city, holds no cookie for this session and is not its actor, so a session id in that link would
   * open nothing. The parameter is named for the shape it is bound to, not for what it carries, and
   * both kinds are legal here — Karti opening his own session's link passes a `ses_` id.
   *
   * This page previously fed the raw parameter straight to `GET /sessions/:id` and to the SSE. For a
   * `vox_` token that is two 404s: the header stayed on the fallback name, the stream light stayed
   * off, and §3.15's whole purpose — Karti being able to see what her screen says — failed in exactly
   * the silent way it was written to prevent.
   */
  const { sessionId: handle = "" } = useParams();
  const [events, setEvents] = useState<Timed[]>([]);
  const [level, setLevel] = useState<{ peakDbfs: number; shortTermLufs: number } | null>(null);
  const [connected, setConnected] = useState(false);
  const [copyFailures, setCopyFailures] = useState<string[]>([]);
  const seq = useRef(0);
  /** Told apart so the header can say "connecting" once and "the connection dropped" ever after. */
  const everConnected = useRef(false);

  // The subject's name and the stored `/health` come with the resolution, rather than being waited
  // for on the stream: a helper who opens the link ten minutes in must not see an empty page until
  // the next event fires.
  const { session, sessionId, claimToken } = useSessionHandle(handle, "live");

  /**
   * §3.13's sentence. Fetched on its own route rather than off `SessionView`, because `SessionView`
   * is what her studio polls and the sentence must not be one careless render away from her screen.
   */
  const [reveal, setReveal] = useState<{ text: string; revealed: boolean } | null>(null);
  const [revealDraft, setRevealDraft] = useState("");
  const [revealOutcome, setRevealOutcome] = useState<string | null>(null);
  const [revealSaving, setRevealSaving] = useState(false);

  useEffect(() => {
    if (sessionId === null) return;
    const ac = new AbortController();
    api.revealText(sessionId, ac.signal, claimToken).then(
      (r) => {
        setReveal({ text: r.revealText ?? "", revealed: r.revealed });
        setRevealDraft(r.revealText ?? "");
      },
      () => {
        // A helper whose claim token does not confer `canEdit` simply does not get the field. This is
        // the console, so the absence is legible: the panel is not drawn at all rather than drawn
        // dead. Nothing is said to him about why — `readAccess` refuses 404 either way (VOX-LAW 18).
      },
    );
    return () => ac.abort();
  }, [sessionId, claimToken]);

  const saveReveal = useCallback((): void => {
    if (sessionId === null || revealSaving) return;
    setRevealSaving(true);
    api.setRevealText(sessionId, revealDraft.trim(), claimToken).then(
      (r) => {
        setReveal({ text: r.revealText ?? "", revealed: r.outcome === "already-revealed" });
        setRevealOutcome(r.outcome);
        setRevealSaving(false);
      },
      (err: unknown) => {
        // The console gets the engineering detail, in full. §3.15: "the one place in the product
        // where a stack trace is the right answer, because the reader is the person who can act."
        const d = describeForConsole(err);
        setRevealOutcome(`${d.where}: ${d.message}`);
        setRevealSaving(false);
      },
    );
  }, [sessionId, claimToken, revealDraft, revealSaving]);

  useEffect(() => {
    if (sessionId === null) return;
    // The resolved `ses_` id, and the token that gives this guest the right to watch it.
    const es = openLive(sessionId, claimToken);
    es.onopen = () => {
      everConnected.current = true;
      setConnected(true);
    };
    /**
     * `onerror` on an `EventSource` fires for a dropped connection *and* for a refusal, and the
     * browser retries the first case by itself. So this marks the light and says nothing else —
     * there is no reconnect button, because adding one would mean racing the browser's own retry
     * and ending with two streams on a session that has exactly one writer.
     */
    es.onerror = () => setConnected(false);
    es.onmessage = (m: MessageEvent<string>) => {
      let ev: LiveEvent;
      try {
        ev = readLiveEvent(m.data);
      } catch (err) {
        // A malformed event on the console is shown, not swallowed. This is the one screen where
        // the reader can do something about it.
        const d = describeForConsole(err);
        setEvents((prev) =>
          [
            ...prev,
            { t: "error", where: "live-event", message: d.message, stack: d.stack, at: Date.now(), seq: seq.current++ } as Timed,
          ].slice(-KEEP),
        );
        return;
      }
      if (ev.t === "level") {
        // Folded, never appended. See the note on KEEP.
        setLevel({ peakDbfs: ev.peakDbfs, shortTermLufs: ev.shortTermLufs });
        return;
      }
      setEvents((prev) => [...prev, { ...ev, seq: seq.current++ }].slice(-KEEP));
    };
    return () => es.close();
  }, [sessionId, claimToken]);

  /** The other half of `lib/say.ts`'s promise: a copy hole reaches the person who can fix it. */
  useEffect(() => {
    const prev = setCopyFailureSink((f) => setCopyFailures((all) => [...all, `${f.detail}`].slice(-20)));
    return () => {
      setCopyFailureSink(prev);
    };
  }, []);

  const lastPrompt = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]!;
      if (e.t === "prompt") return e;
    }
    return null;
  }, [events]);

  const lastState = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]!;
      if (e.t === "state" || e.t === "ended") return e;
    }
    return null;
  }, [events]);

  const lastDrain = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]!;
      if (e.t === "drain") return e;
    }
    return null;
  }, [events]);

  const lastError = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]!;
      if (e.t === "error") return e;
    }
    return null;
  }, [events]);

  // `flatMap` and not `filter`: a `filter` needs a hand-written type predicate to narrow a
  // discriminated union, and a hand-written predicate is a cast wearing a signature — it compiles
  // whether or not it is true. `flatMap` narrows from the `if` and cannot lie.
  const coachLines = useMemo(() => events.flatMap((e) => (e.t === "coach" ? [e] : [])), [events]);
  const takes = useMemo(() => events.flatMap((e) => (e.t === "take" ? [e] : [])), [events]);

  // Before the session resolves there is no name to show, so the handle from the URL stands in. It is
  // the one string Karti can read down a phone to say which session he is looking at.
  const name = session?.subjectName ?? handle;
  const state = lastState?.state ?? session?.state ?? null;
  const ended = lastState?.t === "ended";

  return (
    <ConsoleFrame>
      <header>
        <p className="vox-eyebrow">
          <StatusDot state={connected ? "up" : ended ? "asleep" : "down"} />
          {state ? CONSOLE.state[state] : ""}
        </p>
        <h1>{say(CONSOLE.live.title, { name }, CONSOLE.live.title)}</h1>
        {/*
          The stream light, in words as well as in a colour. §3.15's whole job is to replace the
          phone call "what does it say on the screen?", and a helper who cannot tell "connecting" from
          "the session is over" from "my wifi dropped" is a helper who phones anyway. `lost` says
          explicitly that the browser is retrying, because there is deliberately no reconnect button
          — adding one would race `EventSource`'s own retry and end with two streams on a session
          that has exactly one writer.
        */}
        {ended ? (
          <p className="vox-quiet">{CONSOLE.live.ended}</p>
        ) : connected ? (
          <p className="vox-quiet">{events.length === 0 ? CONSOLE.live.watching : ""}</p>
        ) : (
          <p className="vox-quiet">{everConnected.current ? CONSOLE.live.lost : CONSOLE.live.connecting}</p>
        )}
      </header>

      <section>
        <dl className="vox-facts">
          <dt>{CONSOLE.live.prompt}</dt>
          <dd>
            {lastPrompt ? (
              promptLabel(lastPrompt.promptId, lastPrompt.index, lastPrompt.total)
            ) : (
              <Empty>{CONSOLE.live.noPrompt}</Empty>
            )}
          </dd>

          {/*
            The level meter. It is a number AND a bar, because a bar alone cannot be read down a
            phone line — which is the medium this whole screen exists to replace. dBFS, not a
            percentage: `peakDbfs` is what the browser measured (VOX-LAW 13, advisory, never written
            to the database) and renaming it into a percentage here would invent a scale.
          */}
          <dt>{CONSOLE.live.level}</dt>
          <dd>
            {level ? (
              <Meter peakDbfs={level.peakDbfs} shortTermLufs={level.shortTermLufs} />
            ) : (
              <Empty>{CONSOLE.live.noLevel}</Empty>
            )}
          </dd>

          <dt>{CONSOLE.live.uploads}</dt>
          <dd>
            {lastDrain ? `${lastDrain.saved} / ${lastDrain.total}` : `${session?.takes.length ?? takes.length} stored`}
          </dd>

          {/*
            §3.14 point 1: "`GET /health` is probed before prompt 1, and the full response —
            including `model_loaded`, `dtype` and the variant list — is stored on the session row. A
            session recorded during an outage is then legible afterwards as an outage rather than as
            an unexplained quality outlier."

            So it is rendered verbatim, `raw` included, and not summarised into a green tick.
          */}
          <dt>{CONSOLE.live.health}</dt>
          <dd>
            {session?.health ? (
              <span className="vox-mono">
                modelLoaded={String(session.health.modelLoaded)} dtype={session.health.dtype ?? "null"} rtt=
                {session.health.rttMs}ms variants=[{session.health.variants.join(", ")}]
              </span>
            ) : (
              <Empty>{CONSOLE.live.noHealth}</Empty>
            )}
          </dd>
        </dl>
      </section>

      {/*
        ════════════════════════════════════════════════════════════════════════
        §3.13's ONE INPUT. See the file header for why it is on this screen and
        why it is the only write here.
        ════════════════════════════════════════════════════════════════════════
        It is placed high — directly under the facts, above the coach log — because it is the one
        thing on this page that is *actionable* and the one thing with a deadline: written before the
        clone lands it rides the chain, written after it starts the payoff on its own, and never
        written at all is a session that ends deferred for a reason no operator could previously see.

        Drawn only when the route answered, i.e. only for a caller `canEdit` admits. A dead field on
        a console is worse than no field: it invites a helper to type a sentence into a box that
        cannot save it, at the one moment he is not going to check.
      */}
      {reveal !== null && (
        <section>
          <h2>{CONSOLE.live.revealTitle}</h2>
          <p className="vox-quiet">{CONSOLE.live.revealHelp}</p>
          {reveal.revealed ? (
            <>
              <p className="vox-quiet">{CONSOLE.live.revealAlready}</p>
              <p className="vox-read">{reveal.text}</p>
            </>
          ) : (
            <>
              <Field
                label={CONSOLE.live.revealTitle}
                labelHidden
                value={revealDraft}
                placeholder={CONSOLE.live.revealPlaceholder}
                maxLength={REVEAL_TEXT_MAX_CHARS}
                onChange={(e) => setRevealDraft(e.currentTarget.value)}
                // Enter saves. This is a one-line field on a console and the helper is on a phone
                // call — reaching for a button after typing a sentence is a step that gets skipped.
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveReveal();
                }}
              />
              <div className="vox-btn-row" style={{ marginTop: "var(--space-3)" }}>
                <Button variant="primary" onClick={saveReveal} aria-busy={revealSaving}>
                  {CONSOLE.live.revealSave}
                </Button>
              </div>
              {revealOutcome === null ? (
                reveal.text === "" ? (
                  <p className="vox-quiet">{CONSOLE.live.revealEmpty}</p>
                ) : null
              ) : (
                <p className="vox-quiet" role="status" aria-live="polite">
                  {revealOutcome === "speaking"
                    ? CONSOLE.live.revealSpeaking
                    : revealOutcome === "stored-no-rev"
                      ? CONSOLE.live.revealNoRev
                      : revealOutcome === "already-revealed"
                        ? CONSOLE.live.revealAlready
                        : revealOutcome === "stored-early"
                          ? CONSOLE.live.revealSaved
                          : // Anything else is a wire failure, printed verbatim: this is the console.
                            revealOutcome}
                </p>
              )}
            </>
          )}
        </section>
      )}

      <section>
        <h2>{CONSOLE.live.coachLines}</h2>
        {/*
          §3.9's budget, spent out of `COACH_BUDGET` (2, for the entire session, ever). It is here
          rather than in the fact list above because `CONSOLE.live.coachBudget` is a whole sentence
          with two holes — "{n} of {total} corrections used" — and a sentence is not a label.

          It reads `session.coachSpent` in preference to counting the lines on this stream, because a
          helper who opened the link late has missed events and `coachSpent` is the server's count.
          Falling back to the stream's own count is right for the case where the session row has not
          been re-fetched since the last correction fired.
        */}
        <p className="vox-quiet">
          {say(
            CONSOLE.live.coachBudget,
            { n: session?.coachSpent ?? coachLines.length, total: COACH_BUDGET },
            CONSOLE.live.coachBudget,
          )}
        </p>
        {coachLines.length === 0 ? (
          <Empty>{CONSOLE.live.noCoach}</Empty>
        ) : (
          <ul className="vox-rows">
            {coachLines.map((c) => (
              <li key={c.seq} className="vox-row">
                <span className="vox-chip vox-chip-live">{c.spent}/{c.budget}</span>
                <span>{c.line}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2>{CONSOLE.live.lastError}</h2>
        {lastError ? (
          // Full detail, stack included. §3.15. VOX-LAW 16b governs the other end: the same failure
          // renders to her as one warm sentence, and that asymmetry is the design.
          <pre className="vox-log">
            {lastError.where}: {lastError.message}
            {lastError.stack ? `\n${lastError.stack}` : ""}
          </pre>
        ) : (
          <p className="vox-quiet">{CONSOLE.live.noError}</p>
        )}
      </section>

      {copyFailures.length > 0 && (
        <section>
          {/*
            A copy hole is a defect of the same severity as a wire error and it produces no error
            anywhere by itself — `say()` swallows it into a warm sentence, which is correct for her
            and useless for anybody who could fix it. This is where it surfaces. There is no
            `CONSOLE` string for it because it is a developer artefact rather than a reported fact.
          */}
          <h2>{CONSOLE.live.copyHoles}</h2>
          <pre className="vox-log vox-notice-hard">{copyFailures.join("\n")}</pre>
        </section>
      )}

      <section>
        <h2>{CONSOLE.live.events}</h2>
        {events.length === 0 ? (
          <Empty>{CONSOLE.live.noEvents}</Empty>
        ) : (
          <pre className="vox-log">
            {events.map((e) => `${new Date(e.at).toISOString().slice(11, 23)}  ${e.t}  ${summarise(e)}`).join("\n")}
          </pre>
        )}
      </section>
    </ConsoleFrame>
  );
}

/**
 * dBFS drawn as a bar, with the number beside it.
 *
 * The scale is −60 → 0 dBFS. −60 is `SILENCE_FLOOR_LUFS` in `shared/audio/loudness.ts`, i.e. the
 * level below which the loudness meter itself stops counting a block as audio, so it is the right
 * floor for a picture of "is anything arriving" rather than an arbitrary round number.
 */
function Meter({ peakDbfs, shortTermLufs }: { peakDbfs: number; shortTermLufs: number }) {
  const pct = Math.max(0, Math.min(100, ((peakDbfs + 60) / 60) * 100));
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-3)" }}>
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          width: "8rem",
          height: "0.5rem",
          borderRadius: "var(--radius-pill)",
          background: "var(--meter-floor)",
          overflow: "hidden",
        }}
      >
        <span
          style={{
            display: "block",
            width: `${pct}%`,
            height: "100%",
            // The hot colour marks the region where a take is at risk of the true-peak reject
            // (§5.3, `TRUE_PEAK_REJECT_DBTP` = −0.1 dBTP). −3 dBFS is `SEED_POLICY.deliveredPeakDbfs`
            // and is the level the delivered seed is normalised to, so anything above it is headroom
            // already spent.
            background: peakDbfs > -3 ? "var(--meter-hot)" : "var(--meter-ink)",
          }}
        />
      </span>
      <span className="vox-mono">
        peak {peakDbfs.toFixed(1)} dBFS · short-term {shortTermLufs.toFixed(1)} LUFS
      </span>
    </span>
  );
}

/** `p03 (2 of 5) — "…"`. The prompt text is included because "p03" answers nothing on a phone call. */
function promptLabel(promptId: string, index: number, total: number): string {
  let text = "";
  try {
    text = promptById(promptId).text;
  } catch (err) {
    if (!(err instanceof ScriptError)) throw err;
    text = "(not in this build's script.ts)";
  }
  return `${promptId} (${index + 1} of ${total}) — ${text}`;
}

/** One line per event in the raw log. Deliberately terse; the detail is in the panels above. */
function summarise(e: LiveEvent): string {
  switch (e.t) {
    case "state":
      return e.state;
    case "prompt":
      return `${e.promptId} ${e.index + 1}/${e.total}`;
    case "level":
      return `${e.peakDbfs.toFixed(1)} dBFS`;
    case "coach":
      return `${e.spent}/${e.budget} ${e.line}`;
    case "take":
      return `${e.take.promptId} attempt ${e.take.attempt} ${e.take.durationMs}ms ${e.take.bytes}B ${
        e.take.seedEligible ? "eligible" : (e.take.ineligibleReason ?? "ineligible")
      }`;
    case "drain":
      return `${e.saved}/${e.total}`;
    case "error":
      return `${e.where}: ${e.message}`;
    case "ended":
      return e.state;
  }
}
